// Rhyme Detection Utility for Songwriting
// Phonetic rhyme detection with slant rhyme support — backend-backed lookup
// Local CMU dict removed in scope 13-04; backend covers the full 134K-word CMU dict

import { stripStress, isVowel } from './rhyme/phonetics'

/**
 * Legacy rhyme detection result (used by detectRhymeScheme)
 * Note: This is the old API. For new categorical rhyme detection,
 * use detectRhymeCategory() which returns RhymeMatch with discrete categories.
 */
export interface RhymeResult {
  lineIndex: number
  rhymeLetter: string // A, B, C, etc.
  endWord: string
  phonemes?: string[] // CMU phonemes if available
  rhymesWith: number[] // Indices of lines this rhymes with
}

export interface RhymeAnalysis {
  lines: RhymeResult[]
  pattern: string // e.g., "AABB", "ABAB"
  sectionType?: string // verse, chorus, etc. if provided
}

export interface RhymeDetectorOptions {
  includeSlantRhymes?: boolean // default true
  sectionType?: string // optional section context
  useBackend?: boolean // default true - backend is now the only phoneme source
}

/**
 * Extract the last word from a line, removing punctuation
 */
export function extractEndWord(line: string): string {
  const trimmed = line.trim()
  if (!trimmed) return ''

  const words = trimmed.split(/\s+/)
  const lastWord = words[words.length - 1]

  // Strip punctuation but keep apostrophes for contractions
  return lastWord.toLowerCase().replace(/[^a-z']/g, '')
}

/**
 * Look up CMU phonemes for a word and return the rhyming portion.
 * Backend is the source of truth — local CMU dict removed in scope 13-04.
 *
 * @param word - The word to look up
 * @param useBackend - Enable backend lookup (default: true)
 * @returns Rhyming phonemes or null if word cannot be resolved
 */
export async function getEndWordPhonemes(
  word: string,
  useBackend: boolean = true
): Promise<string[] | null> {
  const normalized = word.toLowerCase().replace(/'/g, '')

  // Local CMU dict removed — backend covers the full 134K-word CMU dict
  if (useBackend) {
    const backendPhonemes = await fetchPhonemesFromBackend(normalized)
    if (backendPhonemes) {
      return extractRhymingPhonemes(backendPhonemes)
    }
  }

  return null
}

/**
 * Fetch phonemes from backend /api/prosody endpoint
 * Uses 100ms timeout and gracefully degrades on failure
 *
 * @param word - The word to look up
 * @returns Phonemes from backend or null on failure
 */
async function fetchPhonemesFromBackend(word: string): Promise<string[] | null> {
  // Guard: skip if offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.debug('[rhymeDetector] Offline - skipping backend lookup for:', word)
    return null
  }

  try {
    // AbortController for 5000ms timeout (increased from 100ms for Docker proxy)
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    const response = await fetch('/api/prosody/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lines: [word] }),
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      console.debug('[rhymeDetector] Backend returned error:', response.status)
      return null
    }

    const data = await response.json()

    // Extract phonemes from first line's rhyme data
    if (data.lines && data.lines[0] && data.lines[0].rhyme) {
      const phonemes = data.lines[0].rhyme.phonemes
      if (Array.isArray(phonemes) && phonemes.length > 0) {
        return phonemes
      }
    }

    return null
  } catch (error) {
    // Gracefully degrade on timeout, network error, etc.
    if (error instanceof Error && error.name === 'AbortError') {
      console.debug('[rhymeDetector] Backend timeout for:', word)
    } else {
      console.debug('[rhymeDetector] Backend error:', error)
    }
    return null
  }
}

/**
 * Extract rhyming phonemes (from last stressed vowel onward)
 * CMU stress markers: 0 (no stress), 1 (primary), 2 (secondary)
 */
export function extractRhymingPhonemes(phonemes: string[]): string[] {
  if (!phonemes || phonemes.length === 0) return []

  // Find the last stressed vowel (1 or 2)
  let lastStressedIndex = -1
  for (let i = phonemes.length - 1; i >= 0; i--) {
    if (phonemes[i].match(/[12]$/)) {
      lastStressedIndex = i
      break
    }
  }

  // If no stressed vowel found, use entire phoneme sequence
  if (lastStressedIndex === -1) return phonemes

  // Return from last stressed vowel to end
  return phonemes.slice(lastStressedIndex)
}

/**
 * Compare phoneme similarity for rhyme detection
 * Returns 0-1 similarity score
 * Note: Phonemes are expected to already be the rhyming portion (from getEndWordPhonemes)
 */
export function phonemeSimilarity(p1: string[], p2: string[]): number {
  if (!p1 || !p2 || p1.length === 0 || p2.length === 0) return 0

  // Exact match = perfect rhyme
  if (p1.join(' ') === p2.join(' ')) return 1.0

  // Check for assonance: shared stressed vowel at start of rhyming portion
  // Rhyming portions start with the stressed vowel (e.g., seam: IY1 M, pieces: IY1 S AH0 Z, ease: IY1 Z)
  // If the first phoneme (stressed vowel) matches, it's an assonance rhyme
  const firstVowel1 = p1.length > 0 && isVowel(p1[0]) ? stripStress(p1[0]) : null
  const firstVowel2 = p2.length > 0 && isVowel(p2[0]) ? stripStress(p2[0]) : null

  if (firstVowel1 && firstVowel2 && firstVowel1 === firstVowel2) {
    // Assonance detected - same stressed vowel
    // Score based on how many following phonemes also match
    // Minimum score of 0.7 (threshold) for assonance
    const minLength = Math.min(p1.length, p2.length)
    const maxLength = Math.max(p1.length, p2.length)

    let matches = 1 // Vowel match counts
    for (let i = 1; i < minLength; i++) {
      if (stripStress(p1[i]) === stripStress(p2[i])) {
        matches++
      }
    }

    // Assonance base score + partial match bonus
    const overlapRatio = matches / maxLength
    return Math.max(0.7, overlapRatio) // Ensure at least 0.7 for assonance
  }

  // Calculate overlap for slant rhymes (existing logic)
  const minLength = Math.min(p1.length, p2.length)
  const maxLength = Math.max(p1.length, p2.length)

  let matches = 0
  for (let i = 0; i < minLength; i++) {
    // Compare phonemes from the end (rhymes work backwards)
    const idx1 = p1.length - 1 - i
    const idx2 = p2.length - 1 - i

    if (p1[idx1] === p2[idx2]) {
      matches++
    }
  }

  // Score based on overlap ratio
  return matches / maxLength
}

/**
 * Fallback suffix similarity for words not in CMU dictionary
 */
export function suffixSimilarity(w1: string, w2: string): number {
  if (!w1 || !w2) return 0

  const clean1 = w1.toLowerCase().replace(/[^a-z]/g, '')
  const clean2 = w2.toLowerCase().replace(/[^a-z]/g, '')

  // Identical words that aren't in dictionary shouldn't auto-match
  // (they need phonetic data for reliable rhyme detection)
  if (clean1 === clean2) return 0.5

  if (clean1.length < 2 || clean2.length < 2) return 0

  // Compare last 2-3 characters (simple rhyme sound approximation)
  const suffix1 = clean1.slice(-3)
  const suffix2 = clean2.slice(-3)

  if (suffix1 === suffix2) return 0.8 // Good match but not perfect (no phonetics)

  // Check last 2 characters
  if (clean1.slice(-2) === clean2.slice(-2)) return 0.6

  // Check if they end with same vowel (assonance)
  const lastChar1 = clean1.slice(-1)
  const lastChar2 = clean2.slice(-1)

  if (/[aeiou]/.test(lastChar1) && lastChar1 === lastChar2) {
    return 0.3
  }

  return 0
}

/**
 * Main rhyme detection function
 * Analyzes lines and assigns rhyme scheme letters
 */
export async function detectRhymeScheme(
  lines: string[],
  options: RhymeDetectorOptions = {}
): Promise<RhymeAnalysis> {
  const {
    includeSlantRhymes = true,
    sectionType,
    useBackend = true  // Local CMU dict removed — backend is the only phoneme source
  } = options

  const results: RhymeResult[] = []
  const schemeLetters: string[] = []
  const rhymeGroups: Map<string, number[]> = new Map()
  let nextLetter = 'A'
  const minSimilarity = 0.7

  // Process each line
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()

    // Skip empty lines
    if (!line) continue

    const endWord = extractEndWord(line)
    if (!endWord) continue

    const phonemes = await getEndWordPhonemes(endWord, useBackend)
    let assigned = false
    let bestMatchIndex = -1
    let bestSimilarity = 0

    // Check against all previous lines for rhymes
    for (let j = 0; j < results.length; j++) {
      const prevResult = results[j]
      let similarity = 0

      // Try phonetic matching first
      if (phonemes && prevResult.phonemes) {
        similarity = phonemeSimilarity(phonemes, prevResult.phonemes)
      } else {
        // Fallback to suffix matching
        similarity = suffixSimilarity(endWord, prevResult.endWord)
      }

      // Check if this is a valid rhyme match
      const meetsThreshold = includeSlantRhymes
        ? similarity >= minSimilarity
        : similarity === 1.0

      if (meetsThreshold && similarity > bestSimilarity) {
        bestSimilarity = similarity
        bestMatchIndex = j
      }
    }

    // Assign rhyme letter
    if (bestMatchIndex >= 0) {
      // Rhymes with existing line
      const matchingLetter = results[bestMatchIndex].rhymeLetter
      schemeLetters.push(matchingLetter)

      // Update rhyme group
      const groupKey = matchingLetter
      const group = rhymeGroups.get(groupKey) || []
      if (!group.includes(bestMatchIndex)) {
        group.push(bestMatchIndex)
      }
      group.push(i)
      rhymeGroups.set(groupKey, group)

      assigned = true
    }

    if (!assigned) {
      // New rhyme - assign next letter
      schemeLetters.push(nextLetter)
      rhymeGroups.set(nextLetter, [i])
      nextLetter = String.fromCharCode(nextLetter.charCodeAt(0) + 1)
    }

    // Create result for this line
    const letter = schemeLetters[schemeLetters.length - 1]
    const rhymesWithIndices = (rhymeGroups.get(letter) || []).filter(idx => idx !== i)

    results.push({
      lineIndex: i,
      rhymeLetter: letter,
      endWord,
      phonemes: phonemes || undefined,
      rhymesWith: rhymesWithIndices
    })

    // Update rhymesWith for all other results in this rhyme group
    if (rhymesWithIndices.length > 0) {
      for (const rhymeIndex of rhymesWithIndices) {
        const targetResult = results[rhymeIndex]
        if (targetResult && !targetResult.rhymesWith.includes(i)) {
          targetResult.rhymesWith.push(i)
        }
      }
    }
  }

  return {
    lines: results,
    pattern: schemeLetters.join(''),
    sectionType
  }
}

// Re-export categorical rhyme detection API — now lives in rhyme/categoryDetector
export { detectRhymeCategory } from './rhyme/categoryDetector';
export { RhymeCategory, CATEGORY_WEIGHTS } from './rhyme/types';
export type { RhymeMatch, RhymeMatch as CategoricalRhymeMatch } from './rhyme/types';
